import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Shield, User, CheckCircle2, Clock, AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import { Button, Input, Card, CardContent, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from '../ui'
import { getEstadoCuentaPublico } from '../../lib/supabase'
import ReportarPagoModal from './ReportarPagoModal'

const formatMoney = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`

const ESTATUS_COLORS = {
  pendiente: 'bg-amber-100 text-amber-800',
  parcial: 'bg-blue-100 text-blue-800',
  pagado: 'bg-green-100 text-green-800',
  vencido: 'bg-red-100 text-red-800',
}

const ESTATUS_ICONS = {
  pagado: CheckCircle2,
  pendiente: Clock,
  parcial: Clock,
  vencido: AlertTriangle,
}

const EstadoCuentaPublic = () => {
  const { token } = useParams()
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [attempts, setAttempts] = useState(0)
  const [expandedCargos, setExpandedCargos] = useState({})
  const [lastFetchedAt, setLastFetchedAt] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [reportarOpen, setReportarOpen] = useState(false)
  const [reportarCargoInicial, setReportarCargoInicial] = useState(null)
  const [reportarPrefill, setReportarPrefill] = useState(null)
  const [reportarReplaces, setReportarReplaces] = useState(null)
  const validatedPinRef = useRef(null)
  const toggleCargo = (id) => setExpandedCargos((prev) => ({ ...prev, [id]: !prev[id] }))

  const fetchEstado = async ({ usePin, isSilent = false } = {}) => {
    if (isSilent) setRefreshing(true)
    else setLoading(true)
    setError(null)

    const { data: result, error: rpcError } = await getEstadoCuentaPublico(token, usePin)

    if (isSilent) setRefreshing(false)
    else setLoading(false)

    if (rpcError) {
      if (!isSilent) {
        setAttempts((a) => a + 1)
        setError('Error al consultar. Verifica tu PIN.')
      }
      return false
    }

    if (result?.error) {
      if (!isSilent) {
        setAttempts((a) => a + 1)
        setError(result.error)
      }
      return false
    }

    setData(result)
    setLastFetchedAt(new Date())
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!pin || pin.length < 4) return
    if (attempts >= 5) {
      setError('Demasiados intentos. Intenta mas tarde.')
      return
    }

    const ok = await fetchEstado({ usePin: pin })
    if (ok) validatedPinRef.current = pin
  }

  const handleRefresh = () => {
    if (!validatedPinRef.current) return
    fetchEstado({ usePin: validatedPinRef.current, isSilent: true })
  }

  // Auto-refresh al volver a la pestania (visibility change)
  useEffect(() => {
    if (!data || !validatedPinRef.current) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') handleRefresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // PIN entry screen
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0D0D0D' }}>
        <div className="w-full max-w-sm rounded-2xl p-8 space-y-6" style={{ background: '#1A1A1A', border: '1px solid #3A3A3A' }}>
          <div className="text-center">
            <img src="/assets/myklubb-icon.svg" alt="MYKLUBB" className="h-14 w-14 mx-auto mb-3" />
            <h1 className="text-xl font-bold" style={{ color: '#FFFFFF' }}>Estado de Cuenta</h1>
            <p className="text-sm mt-1" style={{ color: '#8A8A8A' }}>Ingresa tu PIN de acceso</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="• • • •"
              className="w-full text-center text-2xl tracking-[0.5em] font-mono rounded-lg py-3 outline-none focus:ring-2"
              style={{
                background: '#252525',
                border: '1px solid #3A3A3A',
                color: '#FFFFFF',
                caretColor: '#B3F000',
              }}
              autoFocus
            />

            {error && (
              <p className="text-sm text-center" style={{ color: '#FF4D4D' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || pin.length < 4}
              className="w-full py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
              style={{
                background: pin.length >= 4 ? '#B3F000' : '#3A3A3A',
                color: pin.length >= 4 ? '#0D0D0D' : '#5A5A5A',
              }}
            >
              {loading ? 'Verificando...' : 'Ver Estado de Cuenta'}
            </button>
          </form>

          <p className="text-[10px] text-center" style={{ color: '#5A5A5A' }}>
            Si no tienes tu PIN, contacta al administrador del club.
          </p>
        </div>
      </div>
    )
  }

  // Estado de cuenta view
  const { familia, org, jugadores = [], saldo_favor = 0 } = data
  const ultimaRechazada = data.ultima_solicitud_rechazada

  const jugadorSaldo = (j) => (j.cargos || []).reduce((s, c) => s + Number(c.saldo || 0), 0)
  const totalGeneral = jugadores.reduce((sum, j) => sum + jugadorSaldo(j), 0)
  const conAdeudo = jugadores.filter((j) => jugadorSaldo(j) > 0).length

  const METODO_LABELS = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia',
    tarjeta: 'Tarjeta',
    saldo_favor: 'Saldo a favor',
    otro: 'Otro',
  }
  const formatFecha = (f) => { if (!f) return ''; try { return new Date(f + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) } catch { return f } }
  const formatRelativa = (date) => {
    if (!date) return ''
    const diff = Math.floor((Date.now() - date.getTime()) / 1000)
    if (diff < 60) return 'hace unos segundos'
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`
    return date.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with club branding */}
      <div className="bg-white border-b px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <img
            src={org?.logo || '/assets/myklubb-icon-light.svg'}
            alt={org?.nombre || 'MYKLUBB'}
            className="h-10 w-10 rounded-lg object-contain"
            onError={(e) => { e.target.src = '/assets/myklubb-icon-light.svg' }}
          />
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg truncate">{org?.nombre || 'Club'}</h1>
            <p className="text-sm text-muted-foreground truncate">Estado de cuenta — {familia?.nombre}</p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            title={lastFetchedAt ? `Actualizado ${formatRelativa(lastFetchedAt)}` : 'Actualizar'}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
        {lastFetchedAt && (
          <div className="max-w-2xl mx-auto pt-1.5 text-[10px] text-muted-foreground/70 text-right">
            Actualizado {formatRelativa(lastFetchedAt)}
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {ultimaRechazada && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4 space-y-2">
              <p className="text-sm text-red-900">
                <strong>Tu pago del {formatFecha(ultimaRechazada.fecha_pago)} fue rechazado.</strong>
              </p>
              <p className="text-sm text-red-800">Motivo: {ultimaRechazada.rechazo_razon}</p>
              <Button
                size="sm" variant="outline"
                onClick={() => {
                  setReportarPrefill({
                    fecha_pago: ultimaRechazada.fecha_pago,
                    monto_total: ultimaRechazada.monto_total,
                  })
                  setReportarReplaces(ultimaRechazada.id)
                  setReportarOpen(true)
                }}
              >
                Corregir y reenviar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{familia?.nombre}</p>
                <p className="text-xs text-muted-foreground">Tutor: {familia?.tutor}</p>
                {jugadores.length > 1 && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {jugadores.length} hijos{conAdeudo > 0 && ` · ${conAdeudo} con adeudo`}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-muted-foreground">Saldo total</p>
                <p className={`text-2xl font-bold ${totalGeneral > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {formatMoney(totalGeneral)}
                </p>
                {Number(saldo_favor) > 0 && (
                  <p className="text-xs text-emerald-600 font-medium">A favor: {formatMoney(saldo_favor)}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Per player */}
        {jugadores.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">No se encontraron datos.</p>
        ) : jugadores.length === 1 ? (
          renderJugadorPanel(jugadores[0])
        ) : (
          <Tabs defaultValue={jugadores[0].id}>
            <TabsList className="h-auto flex-wrap justify-start gap-1 w-full">
              {jugadores.map((j) => {
                const saldo = jugadorSaldo(j)
                return (
                  <TabsTrigger key={j.id} value={j.id} className="gap-1.5">
                    <span>{(j.nombre || '').split(' ')[0]}</span>
                    {saldo > 0 ? (
                      <Badge variant="warning" className="text-[10px] px-1.5 py-0">
                        {formatMoney(saldo)}
                      </Badge>
                    ) : (
                      <Badge variant="success" className="text-[10px] px-1.5 py-0">
                        OK
                      </Badge>
                    )}
                  </TabsTrigger>
                )
              })}
            </TabsList>
            {jugadores.map((j) => (
              <TabsContent key={j.id} value={j.id} className="mt-3">
                {renderJugadorPanel(j)}
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Footer */}
        <p className="text-[10px] text-muted-foreground text-center pt-4">
          Powered by MYKLUBB
        </p>
      </div>

      <ReportarPagoModal
        open={reportarOpen}
        onOpenChange={(o) => {
          setReportarOpen(o)
          if (!o) {
            setReportarCargoInicial(null)
            setReportarPrefill(null)
            setReportarReplaces(null)
          }
        }}
        token={token}
        pin={validatedPinRef.current}
        cargoInicial={reportarCargoInicial}
        otrosCargosJugador={(() => {
          if (!reportarCargoInicial) return []
          const j = jugadores.find((j) => j.cargos?.some((c) => c.id === reportarCargoInicial.id))
          return (j?.cargos || []).filter((c) => c.id !== reportarCargoInicial.id && c.estatus !== 'pagado' && c.saldo > 0)
        })()}
        otrosCargosFamilia={(() => {
          if (!reportarCargoInicial) return []
          const j = jugadores.find((j) => j.cargos?.some((c) => c.id === reportarCargoInicial.id))
          return jugadores
            .filter((other) => other.id !== j?.id)
            .flatMap((other) => (other.cargos || [])
              .filter((c) => c.estatus !== 'pagado' && c.saldo > 0)
              .map((c) => ({ ...c, jugador_nombre: other.nombre }))
            )
        })()}
        replaces={reportarReplaces}
        prefill={reportarPrefill}
        onSuccess={() => {
          // El modal mismo muestra pantalla de exito (step 4) y se cierra
          // cuando el usuario da Cerrar. Aqui solo refrescamos para que
          // los cargos seleccionados se vean como 'En revision'.
          handleRefresh()
        }}
      />
    </div>
  )

  function renderJugadorPanel(jugador) {
    const playerCargos = jugador.cargos || []
    const playerSaldo = jugadorSaldo(jugador)

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{jugador.nombre}</span>
            {jugador.numero && <Badge variant="outline" className="text-xs">#{jugador.numero}</Badge>}
          </div>
          <span className={`text-sm font-medium ${playerSaldo > 0 ? 'text-amber-600' : 'text-green-600'}`}>
            {formatMoney(playerSaldo)}
          </span>
        </div>

        <Card>
          <div className="divide-y">
            {playerCargos.length === 0 ? (
              <div className="px-4 py-3 text-sm text-muted-foreground text-center">Sin cargos</div>
            ) : (
              playerCargos.map((cargo) => {
                const StatusIcon = ESTATUS_ICONS[cargo.estatus] || Clock
                const pagos = (cargo.pagos || [])
                const tienePagos = pagos.length > 0
                const isExpanded = expandedCargos[cargo.id]
                const tieneDescuento = Number(cargo.descuento_porcentaje || 0) > 0

                return (
                  <React.Fragment key={cargo.id}>
                    <div
                      className={`px-4 py-3 flex items-center justify-between ${tienePagos ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                      onClick={() => tienePagos && toggleCargo(cargo.id)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {tienePagos ? (
                          isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <StatusIcon className={`h-4 w-4 shrink-0 ${cargo.estatus === 'pagado' ? 'text-green-600' : cargo.estatus === 'vencido' ? 'text-red-500' : 'text-amber-500'}`} />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{cargo.concepto}</p>
                          {cargo.plan && <p className="text-[10px] text-muted-foreground">{cargo.plan}</p>}
                          {tieneDescuento && cargo.promocion && <p className="text-[10px] text-emerald-600">{cargo.promocion} (-{cargo.descuento_porcentaje}%)</p>}
                          {cargo.fecha_vencimiento && (
                            <p className="text-[10px] text-muted-foreground">Vence: {formatFecha(cargo.fecha_vencimiento)}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {tieneDescuento && (
                          <p className="text-[10px] text-muted-foreground line-through">{formatMoney(cargo.monto_original)}</p>
                        )}
                        {cargo.estatus === 'pagado' ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Pagado</Badge>
                        ) : (
                          <>
                            <span className="text-sm font-medium">{formatMoney(cargo.saldo)}</span>
                            <p className="text-[10px] text-muted-foreground">de {formatMoney(cargo.monto_final)}</p>
                            {cargo.fecha_vencimiento && new Date(cargo.fecha_vencimiento + 'T23:59:59') < new Date() && (
                              <Badge className="bg-red-100 text-red-800 text-[9px] mt-0.5">Retraso</Badge>
                            )}
                          </>
                        )}
                        {cargo.solicitud_pendiente ? (
                          <Badge className="bg-amber-100 text-amber-800 text-xs mt-1 block w-fit ml-auto">En revision</Badge>
                        ) : cargo.estatus !== 'pagado' && cargo.saldo > 0 ? (
                          <Button
                            size="sm" variant="outline" className="text-[10px] h-7 px-2 mt-1"
                            onClick={(e) => {
                              e.stopPropagation()
                              setReportarCargoInicial({
                                id: cargo.id,
                                concepto: cargo.concepto,
                                saldo: cargo.saldo,
                              })
                              setReportarPrefill(null)
                              setReportarReplaces(null)
                              setReportarOpen(true)
                            }}
                          >
                            Reportar pago
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {isExpanded && pagos.map((pago, idx) => (
                      <div key={idx} className="px-4 py-2 bg-muted/30 flex items-center justify-between text-xs border-t border-dashed">
                        <div className="text-muted-foreground">
                          <span className="font-medium text-foreground">{formatFecha(pago.fecha)}</span>
                          {' · '}{METODO_LABELS[pago.metodo] || pago.metodo}
                          {pago.referencia && ` · ${pago.referencia}`}
                        </div>
                        <span className="font-medium text-emerald-700">+{formatMoney(pago.monto)}</span>
                      </div>
                    ))}
                  </React.Fragment>
                )
              })
            )}
          </div>
        </Card>

        {(jugador.equipo || []).length > 0 && (
          <Card>
            <div className="px-4 py-2 border-b">
              <p className="text-xs font-medium text-muted-foreground">Equipo del club</p>
            </div>
            <div className="divide-y">
              {jugador.equipo.map((eq, idx) => {
                const detalle = [eq.talla, eq.modelo].filter(Boolean).join(' · ')
                return (
                  <div key={idx} className="px-4 py-2 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {eq.nombre}{Number(eq.cantidad) > 1 ? ` ×${eq.cantidad}` : ''}
                      </p>
                      {detalle && <p className="text-[10px] text-muted-foreground">{detalle}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <Badge variant="outline" className="text-[10px]">
                        {eq.tipo === 'asignacion' ? 'Asignado' : 'Prestado'}
                      </Badge>
                      {eq.fecha_prestamo && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">{formatFecha(eq.fecha_prestamo)}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        {(jugador.entregables || []).length > 0 && (
          <Card>
            <div className="px-4 py-2 border-b">
              <p className="text-xs font-medium text-muted-foreground">Entregables</p>
            </div>
            <div className="divide-y">
              {jugador.entregables.map((e, idx) => (
                <div key={idx} className="px-4 py-2 flex items-center justify-between gap-2">
                  <span className="text-sm">🎽 {e.nombre}</span>
                  {e.cubierto ? (
                    <Badge variant="success" className="text-[10px]">Listo para recoger</Badge>
                  ) : (
                    <Badge variant="warning" className="text-[10px]">
                      {e.regla_tipo === 'parcialidad' && e.hasta_orden
                        ? `Al cubrir Parcialidad ${e.hasta_orden}`
                        : `Faltan ${formatMoney(e.falta_monto)}`}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    )
  }
}

export default EstadoCuentaPublic
